import { useMemo, useState } from "react";
import { Head, router, usePage } from "@inertiajs/react";
import AppLayout from "@/components/AppLayout";
import { type ScopeNode } from "@/components/ChannelMenu";
import { Card } from "@/components/ui/Card";
import {
  plan_actuals_export_sales_path, plan_actuals_index_path, plan_actuals_path, plan_bottom_up_by_day_channel_path,
  plan_bottom_up_by_month_channel_path,
  wholesale_forecast_by_account_path,
} from "@/lib/routes";
import { useKeepScroll } from "@/lib/useKeepScroll";
import { useMarket } from "@/lib/useMarket";
import { type PeriodValue } from "../Revenue/components/PeriodMenu";
import { nextSort, type SortState } from "../Revenue/sort";
import { type ActualsSortKey, readSort, sortActualsRows, sortQuery } from "./actualsSort";
import ActualsDateLine from "./components/ActualsDateLine";
import ActualsOverview from "./components/ActualsOverview";
import ActualsPaths from "./components/ActualsPaths";
import ActualsToolbar, { type PeriodKey } from "./components/ActualsToolbar";
import ActualsTable, { type ActualsFigures, type ActualsRow, type RowKind } from "./components/ActualsTable";
import SectionHeading from "./components/progress/SectionHeading";
import type { ProgressProps } from "./components/progress/types";
import PlanSwitcher, { type PlanSwitcherData } from "./TopDown/components/PlanSwitcher";

interface PickedNode { id: string; name: string; slug: string }

interface Props {
  plan: {
    id: string;
    name: string;
    slug: string;
    begin_date: string;
    end_date: string;
    currency: string | null;
    org_node: { id: string; name: string };
  };
  period: { key: PeriodKey | "custom"; actual_from: string; actual_to: string; plan_from: string; plan_to: string };
  // Whole months read By Month at the target price, part of a month By Day.
  plan_source: "by_month" | "by_day";
  wholesale_monthly_only: boolean;
  scope_tree: ScopeNode[];
  scope: PickedNode;
  category_tree: ScopeNode[];
  // The picked plan node; null means all categories.
  category: PickedNode | null;
  progress: ProgressProps;
  row_kind: RowKind;
  rows: ActualsRow[];
  total: ActualsFigures;
  can: { view_gross_margin: boolean };
  plan_switcher: PlanSwitcherData;
}

// Everything a page link carries. Both sections share one scope and category.
interface Choices {
  scope: string;
  category: string;
  rows: RowKind;
  period: PeriodValue<PeriodKey>;
  sort: SortState;
}

function matches(row: ActualsRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.name, row.full_name, row.sku, row.plan_node].some((v) => v?.toLowerCase().includes(q));
}

const periodQuery = (p: PeriodValue<PeriodKey>) =>
  p.preset === "custom" ? { period: "custom", from: p.from, to: p.to } : { period: p.preset };

export default function Actuals({
  plan, period, plan_source, wholesale_monthly_only, scope_tree, scope, category_tree, category,
  progress, row_kind, rows, total, can, plan_switcher,
}: Props) {
  const market = useMarket();
  const { url } = usePage();
  const [query, setQuery] = useState("");
  // Changing any picker keeps the page where it was scrolled to.
  const rootRef = useKeepScroll(plan_actuals_path(market.slug, plan.slug));
  const sortable: ActualsSortKey[] = [
    "actual", ...(can.view_gross_margin ? ["gm" as const] : []), ...(row_kind === "products" ? ["units" as const] : []),
  ];
  const sort = readSort(url, sortable);

  // The picker's value; Custom shows the plan window's dates.
  const periodValue: PeriodValue<PeriodKey> = { preset: period.key, from: period.plan_from, to: period.plan_to };

  // Every link keeps the page's choices and changes only what it names. An
  // empty category slug means all categories, left out of the link.
  const current: Choices = {
    scope: scope.slug, category: category?.slug ?? "", rows: row_kind, period: periodValue, sort,
  };
  const link = (patch: Partial<Choices>) => {
    const c = { ...current, ...patch };
    return plan_actuals_path(market.slug, plan.slug, {
      scope: c.scope, rows: c.rows, ...(c.category && { category: c.category }), ...periodQuery(c.period),
      ...sortQuery(c.sort),
    });
  };

  // Sorting is done here, so only the link changes: no reload from the server.
  const onSort = (key: string) =>
    router.replace({ url: link({ sort: nextSort(sort, key) }), preserveScroll: true, preserveState: true });

  // Unsorted, products show highest actual revenue first. Categories and
  // channels keep the plan's and the org tree's own order, as sent by the server.
  const visible = useMemo(() => {
    const found = rows.filter((r) => matches(r, query));
    const base = row_kind === "products" ? found.sort((a, b) => b.actual - a.actual) : found;
    return sortActualsRows(base, sort);
  }, [rows, query, row_kind, sort.sort, sort.dir]);

  return (
    <AppLayout
      header={
        // Only the page and its plan: scope, period and figures all follow
        // the pickers below, so the plan's own target and dates stay out.
        <div className="flex items-center gap-6">
          <h1 className="m-0 text-base font-bold tracking-tight text-foreground">Plan vs Actual</h1>
          <PlanSwitcher data={plan_switcher} redirectPath={plan_actuals_index_path(market.slug)} />
        </div>
      }
    >
      <Head title={`Plan vs Actual — ${plan.name}`} />
      <div ref={rootRef} className="space-y-4">
        <ActualsOverview
          plan={plan}
          progress={progress}
          scopeTree={scope_tree}
          scope={scope}
          hrefForScope={(slug) => link({ scope: slug })}
          categoryTree={category_tree}
          category={category}
          hrefForCategory={(slug) => link({ category: slug })}
        />

        {/* A full-width rule and extra space mark where Overview ends. */}
        <div className="!mt-8 border-t pt-4">
          <SectionHeading title="Comparison & Breakdown">
            <ActualsToolbar
              scopeTree={scope_tree}
              scope={scope}
              hrefForScope={(slug) => link({ scope: slug })}
              categoryTree={category_tree}
              category={category}
              hrefForCategory={(slug) => link({ category: slug })}
              period={periodValue}
              planBegin={plan.begin_date}
              planEnd={plan.end_date}
              hrefForPeriod={(p) => link({ period: p })}
              rowKind={row_kind}
              hrefForRowKind={(kind) => link({ rows: kind })}
            />
          </SectionHeading>
        </div>

        <Card className="overflow-hidden">
          <ActualsPaths
            scopeTree={scope_tree}
            scopeId={scope.id}
            marketId={plan.org_node.id}
            hrefForScope={(slug) => link({ scope: slug })}
            categoryTree={category_tree}
            categoryId={category?.id ?? null}
            hrefForCategory={(slug) => link({ category: slug })}
          />
          <ActualsDateLine
            period={period}
            planSource={plan_source}
            wholesaleMonthlyOnly={wholesale_monthly_only}
            rowKind={row_kind}
            query={query}
            onQueryChange={setQuery}
            // The export follows the table's choices only.
            exportHref={plan_actuals_export_sales_path(market.slug, plan.slug, {
              scope: scope.slug, ...(category && { category: category.slug }), ...periodQuery(periodValue),
            })}
          />
          <ActualsTable
            rowKind={row_kind}
            rows={visible}
            total={total}
            currency={plan.currency}
            showGm={can.view_gross_margin}
            sort={sort}
            onSort={onSort}
            // Where a product's missing plan is filled in: the page the plan
            // revenue reads. Wholesale has nothing to fill for part of a
            // month: its plan is monthly.
            linkForChannel={(c) => {
              if (c.wholesale) {
                return wholesale_monthly_only
                  ? null
                  : { href: wholesale_forecast_by_account_path(market.slug), label: "fill in Wholesale Forecast" };
              }
              return plan_source === "by_month"
                ? { href: plan_bottom_up_by_month_channel_path(market.slug, plan.slug, c.slug), label: "fill in By Month" }
                : { href: plan_bottom_up_by_day_channel_path(market.slug, plan.slug, c.slug), label: "fill in By Day" };
            }}
            emptyText={query ? `Nothing matches “${query}”.` : "No products are planned here."}
            // A channel or category row drills into it; the picked node's own
            // row (one with nothing below it) stays plain.
            hrefForRow={(r) => {
              if (!r.slug) return null;
              if (row_kind === "channels") return r.id !== scope.id ? link({ scope: r.slug }) : null;
              if (row_kind === "categories") return r.id !== category?.id ? link({ category: r.slug }) : null;
              return null;
            }}
          />
        </Card>
      </div>
    </AppLayout>
  );
}
